// Snitplan: fælles geometri (ren matematik, ingen three/DOM, alt i float64).
//
// Et Snitplan er ét PointView-objekt. Den autoritative model er KUN disse simple tal; Potree (BoxVolume-clip) og IFC (native Three.js
// clippingPlanes) afleder hver deres transform/planer herfra. Ingen Euler/quaternion gemmes som sandhed.
//
//   center    [x, y, z]   verdenskoordinater (typisk UTM, derfor float64 hele vejen)
//   length    m           udstrækning langs snittets længderetning (azimut)
//   height    m           udstrækning i planet vinkelret på længderetningen ("op ad hældningen")
//   thickness m           tykkelse, CENTRERET om planet (0,10 m = 0,05 m til hver side)
//   azimuth   grader      0-360, fra nord med uret; længderetningen (strike) er (sin az, cos az, 0)
//   dip       grader      0-90, hældning fra vandret: 0 = vandret plan, 90 = lodret plan
//   orientation           "horizontal" (dip 0) | "vertical" (dip 90) | "inclined" (dip 0-90)
//   roll      grader      0-360, drejning af boksen i sit eget plan (om normalen n); 0 = som uden rulning. Giver sammen med azimut og
//                         hældning en FRI orientering (3D-håndtagene kan dreje boksen frit; se snitplanFromBasis)
//
// Planets akser (højrehåndet, s x u = n):
//   (uden rulning; med rulning r drejes s og u om n: s' = s cos r + u sin r, u' = -s sin r + u cos r)
//   s (længde)  = ( sin az,            cos az,           0     )
//   u (højde)   = (-cos az * cos dip,  sin az * cos dip, sin dip)   "op ad hældningen"; lodret: (0, 0, 1)
//   n (normal)  = ( cos az * sin dip, -sin az * sin dip, cos dip)   vandret: (0, 0, 1)

export const SnitOrientation = {
	HORIZONTAL: "horizontal",
	VERTICAL: "vertical",
	INCLINED: "inclined",
};

export const SnitDisplayMode = {
	NORMAL: "normal",
	HIGHLIGHT: "highlight",
	ISOLATE: "isolate",   // vis kun indenfor
	OUTSIDE: "outside",   // vis kun udenfor
};

export const SNIT_LIMITS = {
	minLength: 0.01,
	minHeight: 0.01,
	minThickness: 0.001,
};

const DEG = Math.PI / 180;

const num = (value, fallback) => {
	const n = typeof value === "string" ? parseFloat(value.replace(",", ".")) : Number(value);
	return Number.isFinite(n) ? n : fallback;
};

const wrap360 = (a) => ((a % 360) + 360) % 360;
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

export function newSnitId(){
	return "snit_" + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

// Fuldstændig, valideret definition ud fra en delvis. Reglerne:
//   vandret -> dip 0, lodret -> dip 90, skråt -> numerisk dip 0-90; azimut 0-360; mål har et nedre loft.
export function normalizeSnitplan(input = {}, base = {}){
	// felt for felt: ugyldigt input falder tilbage til base (den nuværende værdi), derefter til standarden
	const pick = (key, standard) => num(input[key], num(base[key], standard));
	const baseCenter = Array.isArray(base.center) && base.center.length === 3 ? base.center : [0, 0, 0];
	const center = Array.isArray(input.center) && input.center.length === 3
		? input.center.map((v, i) => num(v, num(baseCenter[i], 0)))
		: baseCenter.map((v) => num(v, 0));

	const wanted = input.orientation !== undefined ? input.orientation : base.orientation;
	const orientation = Object.values(SnitOrientation).includes(wanted) ? wanted : SnitOrientation.VERTICAL;
	let dip = clamp(pick("dip", orientation === SnitOrientation.HORIZONTAL ? 0 : 90), 0, 90);
	if(orientation === SnitOrientation.HORIZONTAL) dip = 0;
	else if(orientation === SnitOrientation.VERTICAL) dip = 90;

	const name = input.name !== undefined ? input.name : base.name;
	return {
		id: input.id || base.id || newSnitId(),
		name: String(name == null ? "" : name),
		center,
		length: Math.max(pick("length", 10), SNIT_LIMITS.minLength),
		height: Math.max(pick("height", 10), SNIT_LIMITS.minHeight),
		thickness: Math.max(pick("thickness", 0.1), SNIT_LIMITS.minThickness),
		azimuth: wrap360(pick("azimuth", 0)),
		dip,
		orientation,
		roll: wrap360(pick("roll", 0)),
	};
}

// {s, u, n}: enhedsvektorer som [x, y, z]
export function snitplanAxes(def){
	const az = def.azimuth * DEG;
	const dp = def.dip * DEG;
	const sa = Math.sin(az), ca = Math.cos(az), sd = Math.sin(dp), cd = Math.cos(dp);
	const s0 = [sa, ca, 0];
	const u0 = [-ca * cd, sa * cd, sd];
	const n = [ca * sd, -sa * sd, cd];
	if(!def.roll) return {s: s0, u: u0, n};
	// rulning: s og u drejes om n (n er uændret)
	const r = def.roll * DEG;
	const sr = Math.sin(r), cr = Math.cos(r);
	return {
		s: [s0[0] * cr + u0[0] * sr, s0[1] * cr + u0[1] * sr, s0[2] * cr + u0[2] * sr],
		u: [-s0[0] * sr + u0[0] * cr, -s0[1] * sr + u0[1] * cr, -s0[2] * sr + u0[2] * cr],
		n,
	};
}

// Omvendt af snitplanAxes: azimut, hældning, orientering og rulning ud fra tre ortonormale, højrehåndede akser (s x u = n) - fx en
// boks' rotation efter at 3D-håndtagene har drejet den. En boks er symmetrisk under 180 graders drejning om en af sine akser, så n
// vendes altid opad (dip 0-90). `fallbackAzimuth` bruges ved et vandret plan (azimut er da ikke bestemt af normalen).
export function snitplanFromBasis(s, u, n, fallbackAzimuth = 0){
	let nn = n, uu = u;
	if(n[2] < 0){
		nn = [-n[0], -n[1], -n[2]];
		uu = [-u[0], -u[1], -u[2]];
	}
	const h = Math.hypot(nn[0], nn[1]);
	let dip = Math.atan2(h, nn[2]) / DEG;
	let orientation = SnitOrientation.INCLINED;
	let azimuth;
	if(dip < 1e-6){
		dip = 0;
		orientation = SnitOrientation.HORIZONTAL;
		azimuth = wrap360(fallbackAzimuth);
	}else{
		if(dip > 90 - 1e-6){
			dip = 90;
			orientation = SnitOrientation.VERTICAL;
		}
		azimuth = wrap360(Math.atan2(-nn[1], nn[0]) / DEG);
	}
	const base = snitplanAxes({azimuth, dip, roll: 0});
	const dot = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
	const roll = wrap360(Math.atan2(dot(s, base.u), dot(s, base.s)) / DEG);
	return {azimuth, dip, orientation, roll};
}

// Boksen Potree-clip bruger: enheds-BoxVolume (-0.5..0.5) med lokal x = længde, y = højde, z = tykkelse.
export function snitplanBox(def){
	return {
		center: def.center.slice(),
		scale: [def.length, def.height, def.thickness],
		axes: snitplanAxes(def),
	};
}

// De seks flader som indad-vendende planer {normal: [x, y, z], constant} (normal . p + constant >= 0 = inde i volumenen) - samme
// konvention som Three.js' clippingPlanes (IFC-adapteren). Regnes i float64 ud fra definitionen. `outward`: normalerne vendes udad -
// sammen med clipIntersection klipper de det indvendige væk ("vis udenfor").
export function snitplanFacePlanes(def, outward = false){
	const {s, u, n} = snitplanAxes(def);
	const half = [def.length / 2, def.height / 2, def.thickness / 2];
	const c = def.center;
	const out = [];
	[s, u, n].forEach((axis, i) => {
		for(const sign of [+1, -1]){
			// fladens punkt = c + sign*half*axis; indadvendt normal = -sign*axis
			const flip = outward ? -1 : 1;
			const nx = -sign * axis[0] * flip, ny = -sign * axis[1] * flip, nz = -sign * axis[2] * flip;
			const px = c[0] + sign * half[i] * axis[0];
			const py = c[1] + sign * half[i] * axis[1];
			const pz = c[2] + sign * half[i] * axis[2];
			out.push({normal: [nx, ny, nz], constant: -(nx * px + ny * py + nz * pz)});
		}
	});
	return out;
}

// azimut/hældning/orientering ud fra en flades normal (fx en IFC-flade). Normalen vendes, så den peger opad (dip 0-90).
// `fallbackAzimuth` bruges, når planet er vandret (azimut ikke bestemt af normalen).
export function orientationFromNormal(normal, fallbackAzimuth = 0){
	let [nx, ny, nz] = normal;
	const len = Math.hypot(nx, ny, nz) || 1;
	nx /= len; ny /= len; nz /= len;
	if(nz < 0){ nx = -nx; ny = -ny; nz = -nz; }
	const dip = Math.acos(clamp(nz, -1, 1)) / DEG;
	const horizontal = Math.hypot(nx, ny);
	let azimuth = fallbackAzimuth;
	if(horizontal > 1e-9) azimuth = wrap360(Math.atan2(-ny, nx) / DEG);
	let orientation = SnitOrientation.INCLINED;
	if(dip < 0.5) orientation = SnitOrientation.HORIZONTAL;
	else if(dip > 89.5) orientation = SnitOrientation.VERTICAL;
	return {
		azimuth,
		dip: orientation === SnitOrientation.HORIZONTAL ? 0 : (orientation === SnitOrientation.VERTICAL ? 90 : dip),
		orientation,
	};
}

// Snitplan udspændt af tre punkter (verdenskoordinater, fx UTM). Planet gennem punkterne giver azimut/hældning/orientering; centrum
// og udstrækning vælges, så de tre punkter ligger inden for planets rektangel (med `margin`). Returnerer en definition-patch
// ({center, length, height, thickness, azimuth, dip, orientation}) eller null, hvis punkterne er (næsten) kollineære eller sammenfaldende.
// Deterministisk: samme tre punkter i samme rækkefølge giver altid samme Snitplan. Længderetningen ved et vandret plan = a -> b.
export function snitplanFromThreePoints(points, {margin = 1.1, minExtent = 0.3, thickness = 0.1} = {}){
	const [a, b, c] = points;
	const ab = [b[0] - a[0], b[1] - a[1], b[2] - a[2]];
	const ac = [c[0] - a[0], c[1] - a[1], c[2] - a[2]];
	const n = [ab[1] * ac[2] - ab[2] * ac[1], ab[2] * ac[0] - ab[0] * ac[2], ab[0] * ac[1] - ab[1] * ac[0]];
	const nl = Math.hypot(n[0], n[1], n[2]);
	const abl = Math.hypot(ab[0], ab[1], ab[2]);
	const acl = Math.hypot(ac[0], ac[1], ac[2]);
	if(!(abl > 0) || !(acl > 0) || !(nl > 1e-3 * abl * acl)) return null;

	const fallbackAzimuth = wrap360(Math.atan2(ab[0], ab[1]) / DEG);
	const o = orientationFromNormal(n, fallbackAzimuth);
	const def = normalizeSnitplan({center: a, azimuth: o.azimuth, dip: o.dip, orientation: o.orientation});
	const {s, u} = snitplanAxes(def);

	let sMin = Infinity, sMax = -Infinity, uMin = Infinity, uMax = -Infinity;
	for(const p of [a, b, c]){
		const d = [p[0] - a[0], p[1] - a[1], p[2] - a[2]];
		const ds = d[0] * s[0] + d[1] * s[1] + d[2] * s[2];
		const du = d[0] * u[0] + d[1] * u[1] + d[2] * u[2];
		sMin = Math.min(sMin, ds); sMax = Math.max(sMax, ds);
		uMin = Math.min(uMin, du); uMax = Math.max(uMax, du);
	}
	const sMid = (sMin + sMax) / 2, uMid = (uMin + uMax) / 2;
	return {
		center: [a[0] + s[0] * sMid + u[0] * uMid, a[1] + s[1] * sMid + u[1] * uMid, a[2] + s[2] * sMid + u[2] * uMid],
		length: Math.max((sMax - sMin) * margin, minExtent),
		height: Math.max((uMax - uMin) * margin, minExtent),
		thickness,
		azimuth: def.azimuth,
		dip: def.dip,
		orientation: def.orientation,
	};
}
